import { getToken } from '../authentication/authProvider';
import { supabase } from '../lib/supabaseClient';

/**
 * Obituary cache invalidation for cleo-web.
 *
 * cleo-web renders `/obituaries/[slug]` with ISR (`revalidate = 3600`), and a
 * `notFound()` render is cached like any other result. A URL requested while the
 * funeral was still `is_live = false` therefore keeps serving a 404 for up to an
 * hour after the obituary is published. Pinging cleo-web on every funeral write
 * clears both the detail page and the listing right away.
 *
 * The ping goes through the NestJS API rather than straight to cleo-web: the shared
 * revalidation secret lives in server env there, whereas anything the admin reads
 * from `process.env` is baked into public JavaScript at build time.
 */

const API_URL = (process.env.REACT_APP_PAPERMAN_API_URL || 'http://localhost:3001/').replace(
  /\/$/,
  ''
);

/**
 * Ask cleo-web to re-render an obituary page. Pass no slug to refresh only the listing.
 * Best-effort: failures are logged, never thrown — a cache ping must not fail a save.
 */
export async function revalidateObituary(slug?: string | null): Promise<void> {
  try {
    const token = await getToken();
    const response = await fetch(`${API_URL}/funerals/revalidate-web`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ slug: slug || undefined }),
    });

    if (!response.ok) {
      console.error(`Failed to revalidate cleo-web obituary cache: HTTP ${response.status}`);
      return;
    }

    const result = await response.json().catch(() => ({}));
    if (result?.revalidated === false) {
      // The API reached us but couldn't reach cleo-web (usually missing env vars).
      console.warn(`cleo-web obituary cache not revalidated: ${result.reason ?? 'unknown reason'}`);
    }
  } catch (error) {
    console.error('Failed to revalidate cleo-web obituary cache:', error);
  }
}

/**
 * Same as `revalidateObituary`, for callers that only hold a funeral id (the memorial
 * photo upload writes to `clients`, so the slug has to be looked up on the funeral).
 */
export async function revalidateObituaryForFuneral(
  funeralId: number | string | undefined | null
): Promise<void> {
  if (!funeralId) return;

  try {
    const { data, error } = await supabase
      .from('funerals')
      .select('slug')
      .eq('id', funeralId)
      .maybeSingle();

    if (error || !data?.slug) return;
    await revalidateObituary(data.slug);
  } catch (error) {
    console.error('Failed to look up funeral slug for revalidation:', error);
  }
}
